import { useIntl } from 'react-intl';
import moment from 'moment';
import { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Grid, Typography, Container, Paper, Divider } from "@mui/material";
import {
    Select,
    Button,
} from '@gpstrackit/react-controls';

//Components
import DataView from 'Components/DataView';
import UserDetails from 'Modules/pmUsers/views/jiraSummary/.components/userDetails';
import InputDate from 'Components/InputDate';

//Notification
import {
    NotificationManager,
} from "Components/Notifications";

//Styles
import './main.scss';

//Actions
import {
    clearRedux,
    jiraSummarySearchRequest,
    jiraDetailsOpenModal,
} from "Redux/actions";

//Helpers
import { getMinDateLimitBehaviour } from 'Core/data/Helpers';
import { getTimeFrame } from 'Utils/helpers';
import { optionsTimeFrame } from 'Core/defaultValues';

const JiraSummary = (props) => {
    const { messages } = useIntl();
    const tableRef = useRef();
    const dispatch = useDispatch();
    let optionsTime = optionsTimeFrame(true);

    const current_week = moment().startOf('week');
    const tempStartDate = new Date(current_week.toString());
    const { developers, loading } = useSelector(state => state.jiraSummaryRedux);
    const { openModal } = useSelector(state => state.userDetailsRedux)
    const [startDate, setStartDate] = useState(moment().day(0).startOf('day').toDate());
    const [endDate, setEndDate] = useState(moment.max(moment()).toDate());
    const [beforeRangeDate, setBeforeRangeDate] = useState({
        start: tempStartDate,
        end: endDate
    })

    const [search, setSearch] = useState(null);
    const [conditions, setConditions] = useState(null);

    const [columns, setColumns] = useState([
        {
            key: "name",
            title: messages['jira_summary_name'],
            width: 200,
            show: true,
            locked: true,
            resize: true,
            export: true,
            exportCell: 'name',
            code: "jira_summary_name",
            render: (item) => {
                return (
                    <span
                        style={{ cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => dispatch(jiraDetailsOpenModal(true, item))}
                    >{item.name}</span>
                )
            }
        },
        {
            key: "country",
            title: messages['jira_summary_country'],
            width: 150,
            show: true,
            resize: true,
            export: true,
            exportCell: 'country',
            code: "jira_summary_country",
            render: (item) => {
                return <span>{item.country}</span>
            }
        },
        {
            key: "daysPeriod",
            title: messages['jira_summary_days_period'],
            width: 150,
            show: true,
            locked: false,
            resize: true,
            export: true,
            exportCell: 'daysPeriod',
            code: "jira_summary_days_period",
            render: (item) => {
                return <span>{item.daysPeriod}</span>
            }
        },
        {
            key: "tasksPeriod",
            title: messages['jira_summary_task_period'],
            width: 150,
            show: true,
            sort: true,
            resize: true,
            export: true,
            exportCell: 'tasksPeriod',
            code: "jira_summary_task_period",
            render: (item) => {
                return <span>{item.tasksPeriod}</span>
            }
        },
        {
            key: "taskPeriodGSD",
            title: messages['jira_summary_task_period_gsd'],
            width: 200,
            show: true,
            sort: true,
            resize: true,
            export: true,
            exportCell: 'taskPeriodGSD',
            code: "jira_summary_task_period_gsd",
            render: (item) => {
                return <span>{item.taskPeriodGSD}</span>
            }
        },
        {
            key: "taskPeriodOthers",
            title: messages['jira_summary_task_period_others'],
            width: 200,
            show: true,
            sort: true,
            resize: true,
            export: true,
            exportCell: 'taskPeriodOthers',
            code: "jira_summary_task_period_others",
            render: (item) => {
                return <span>{item.taskPeriodOthers}</span>
            }
        },
        {
            key: "totalTimeHours",
            title: messages['jira_summary_total_time_hours'],
            width: 150,
            show: true,
            locked: false,
            resize: true,
            export: true,
            exportCell: 'totalTimeHours',
            code: "jira_summary_total_time_hours",
            render: (item) => {
                return <span>{(Math.round(((item.totalTimeHours || 0) + Number.EPSILON) * 100) / 100)}</span>
            }
        },
        {
            key: "totalTimeHoursGSD",
            title: messages['jira_summary_total_time_hours_gsd'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            export: true,
            exportCell: 'totalTimeHoursGSD',
            code: "jira_summary_total_time_hours_gsd",
            render: (item) => {
                return <span>{(Math.round(((item.totalTimeHoursGSD || 0) + Number.EPSILON) * 100) / 100)}</span>
            }
        },
        {
            key: "totalTimeHoursOthers",
            title: messages['jira_summary_total_time_hours_others'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            export: true,
            exportCell: 'totalTimeHoursOthers',
            code: "jira_summary_total_time_hours_others",
            render: (item) => {
                return <span>{(Math.round(((item.totalTimeHoursOthers || 0) + Number.EPSILON) * 100) / 100)}</span>
            }
        },
        {
            key: "totalStoryPoints",
            title: messages['jira_summary_total_story_points'],
            width: 150,
            show: true,
            locked: false,
            resize: true,
            export: true,
            exportCell: 'totalStoryPoints',
            code: "jira_summary_total_story_points",
            render: (item) => {
                return <span>{item.totalStoryPoints}</span>
            }
        },
        {
            key: "totalStoryPointsGSD",
            title: messages['jira_summary_total_story_points_gsd'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            export: true,
            exportCell: 'totalStoryPointsGSD',
            code: "jira_summary_total_story_points_gds",
            render: (item) => {
                return <span>{item.totalStoryPointsGSD}</span>
            }
        },
        {
            key: "totalStoryPointsOthers",
            title: messages['jira_summary_total_story_points_others'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            export: true,
            exportCell: 'totalStoryPointsOthers',
            code: "jira_summary_total_story_points_others",
            render: (item) => {
                return <span>{item.totalStoryPointsOthers}</span>
            }
        },
        {
            key: "totalRejectedTasks",
            title: messages['jira_summary_total_rejected_tasks'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            export: true,
            exportCell: 'totalRejectedTasks',
            code: "jira_summary_total_rejected_tasks",
            render: (item) => {
                return <span>{item.totalRejectedTasks}</span>
            }
        },
        {
            key: "totalRejections",
            title: messages['jira_summary_total_rejections'],
            width: 150,
            show: true,
            locked: false,
            resize: true,
            export: true,
            exportCell: 'totalRejections',
            code: "jira_summary_total_rejections",
            render: (item) => {
                return <span>{item.totalRejections}</span>
            }
        },
        {
            key: "tasksPeriodBugsResponsible",
            title: messages['jira_summary_bugs_responsible'],
            width: 180,
            show: true,
            locked: false,
            resize: true,
            export: true,
            exportCell: 'tasksPeriodBugsResponsible',
            code: "jira_summary_bugs_responsible",
            render: (item) => {
                return <span>{item.tasksPeriodBugsResponsible}</span>
            }
        },
        {
            key: "totalTimeHoursBugs",
            title: messages['jira_summary_total_in_progress_bugs'],
            width: 190,
            show: true,
            locked: false,
            resize: true,
            export: true,
            exportCell: 'totalTimeHoursBugs',
            code: "jira_summary_total_in_progress_bugs",
            render: (item) => {
                return <span>{item.totalTimeHoursBugs}</span>
            }
        },
        {
            key: "totalStoryPointsBugs",
            title: messages['jira_summary_total_story_points_bugs'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            export: true,
            exportCell: 'totalStoryPointsBugs',
            code: "jira_summary_total_story_points_bugs",
            render: (item) => {
                return <span>{item.totalStoryPointsBugs}</span>
            }
        },
        {
            key: "tasksPeriodBugs",
            title: messages['jira_summary_closed_tags_bugs'],
            width: 170,
            show: true,
            locked: false,
            resize: true,
            export: true,
            exportCell: 'tasksPeriodBugs',
            code: "jira_summary_closed_tags_bugs",
            render: (item) => {
                return <span>{item.tasksPeriodBugs}</span>
            }
        },
        {
            key: "quality",
            title: messages['jira_summary_qa_score'],
            width: 150,
            show: true,
            locked: false,
            resize: true,
            export: true,
            exportCell: 'kpi.quality',
            code: "jira_summary_qa_score",
            render: (item) => {
                return <span>{(Math.round(((item.kpi.quality || 0) + Number.EPSILON) * 100) / 100)}</span>
            }
        },
        {
            key: "estimation",
            title: messages['jira_summary_estimation'],
            width: 150,
            show: true,
            locked: false,
            resize: true,
            export: true,
            exportCell: 'kpi.estimation',
            code: "jira_summary_estimation",
            render: (item) => {
                return <span>{item.kpi.estimation}</span>
            }
        },
        {
            key: "participation",
            title: messages['jira_summary_participation'],
            width: 150,
            show: true,
            locked: false,
            resize: true,
            export: true,
            exportCell: 'kpi.participation',
            code: "jira_summary_participation",
            render: (item) => {
                return <span>{item.kpi.participation}</span>
            }
        },
        {
            key: "total",
            title: messages['jira_summary_total'],
            width: 150,
            show: true,
            locked: false,
            resize: true,
            export: true,
            exportCell: 'kpi.total',
            code: "jira_summary_total",
            render: (item) => {
                return (<div>{(Math.round(((item.kpi.total || 0) + Number.EPSILON) * 100) / 100)}</div>)
            }
        },

    ]);

    const [timeFrame, setTimeFrame] = useState(optionsTime[2])



    const applyFilters = (openSearch, refresh, dateRange) => {
        if (openSearch || refresh) {
            setSearch(openSearch || null);
            dispatch(clearRedux("JIRA_SUMMARY", { developers: [] }));
        }
        let currentOpenSearch = !search && !openSearch ? "" : openSearch ? openSearch : search
        let payload = {
            dateRange: {
                start: startDate,
                end: endDate,
            },
            developer: currentOpenSearch
        }

        if (dateRange)
            payload.dateRange = dateRange;

        setConditions(payload);
        dispatch(clearRedux("JIRA_SUMMARY", { developers: [] }))
        dispatch(jiraSummarySearchRequest(payload));

    }

    const handleTimeFrame = (type) => {
        setTimeFrame(type);
        const { start, end } = getTimeFrame(type.Id);
        setStartDate(start);
        setEndDate(end);

        if (type.Id !== "custom")
            applyFilters(null, false, { start: start, end: end });
    }

    const onChangeStartEndDate = ({ start, end }) => {
        const newStart = start && new Date(start);
        const newEnd = end && new Date(end);
        // Set New Date Range
        setStartDate(newStart);
        setEndDate(newEnd);
        if (start && end) {
            const newDateRange = { start, end };
            setBeforeRangeDate(newDateRange)
            applyFilters(null, false, { start: start, end: end });
        }
    }

    const onCloseSelectDate = () => {
        const { start, end } = beforeRangeDate;
        if (start && end && !endDate) {
            setStartDate(start);
            setEndDate(end);
        }
    }

    const onChange = (value) => {
        const { start, end } = value;
        onChangeStartEndDate({
            start,
            end
        })
    };

    useEffect(() => {
        if (search || search === "") {
            const timer = setTimeout(() => {
                applyFilters(search, true);
            }, 700)
            return () => clearTimeout(timer);
        }
    }, [search])

    useEffect(() => {
        applyFilters(search, true);
    }, [])

    return (
        <Container maxWidth={false} className="container-view">
            <Paper elevation={0}>
                <Grid
                    container
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    style={{ padding: "14px 18px", borderBottom: "1px solid #e8e8e8", height: '95v' }}
                >
                    <Typography variant="h5" gutterBottom component="div">
                        {messages['jira_summary_jira_summary']}
                    </Typography>
                    <div style={{ width: '100%', marginBottom: 10, marginTop: 5 }}>
                        <Divider style={{ backgroundColor: '#999999' }} />
                    </div>
                    <DataView
                        ref={tableRef}
                        name="pm-jira-summary-list"
                        exportName="jirasummary"
                        noFilters
                        entity="user-activity-logs"
                        externalData={developers || []}
                        externalLoading={loading}
                        externalRefresh={() => {
                            applyFilters(null, true);
                        }}
                        columns={columns}
                        topheight={200}
                        noSplit
                        hideAllInPagination
                        disableItemSelection
                        infiniteScroll
                        onChangePage={() => {
                            applyFilters();
                        }}
                        onChangeSearch={(value) => {
                            setSearch(value);
                        }}
                        tableButtons={["export", 'orderColumn']}
                        onChangeColumns={(items) => setColumns(items)}
                        noTags
                        noPDF
                        dataGridExport
                        simpleSearch
                        noSearch
                        extraFilters={() => {
                            return (
                                <Grid container style={{ width: "100%", display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                    <Grid item xs={5} style={{ marginRight: 10 }}>
                                        <Select
                                            className="jira-period"
                                            id="timeframe"
                                            placeholder={messages['jira_summary_time_frame']}
                                            options={optionsTime}
                                            value={timeFrame}
                                            textField="Nametag"
                                            valueField="Id"
                                            onChange={(items = []) => handleTimeFrame(items)}
                                        />
                                    </Grid>
                                    {
                                        timeFrame.Id === "custom" &&
                                        <Grid item xs={5} style={{ marginRight: 10 }}>
                                            <InputDate
                                                id="input-date-range"
                                                type="range"
                                                onChange={onChange}
                                                onClose={onCloseSelectDate}
                                                value={startDate}
                                                valueEnd={endDate}
                                                maxDate={new Date()}
                                                minDate={getMinDateLimitBehaviour()}
                                            />
                                        </Grid>
                                    }
                                </Grid>
                            )
                        }}
                    />
                </Grid>
            </Paper>
            {openModal && <UserDetails dataTime={{ start: startDate, end: endDate }} />}
        </Container>
    )
}
export default JiraSummary;

